import React, { useEffect, useRef, useState } from 'react';
import { collection, query, where } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { auth, db } from '../firebase';
import PageShell from './PageShell';
import LoadingState from './LoadingState';
import { ArrowRightIcon, CasesIcon, CloseIcon, DocumentsIcon, LockIcon, PaymentsIcon } from './AppIcons';
import { calculateAgeFromDateOfBirth, extractAadhaarDetails, updateClientProfile } from '../utils/clientProfiles';
import {
  buildClientDraftingSummary,
  genderOptions,
  isClientDraftReady,
  relationLabelOptions,
} from '../utils/draftingProfiles';
import useAiAccessSummary from '../utils/useAiAccessSummary';
import { canUseAiNow, getAiCreditHeadline } from '../utils/billing';
import { useFirestoreCollection } from '../utils/firestoreCache';

const ClientDetails = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { clientId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [client, setClient] = useState(null);
  const [relatedCases, setRelatedCases] = useState([]);
  const [relatedPayments, setRelatedPayments] = useState([]);
  const [relatedDocuments, setRelatedDocuments] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showEdit, setShowEdit] = useState(searchParams.get('edit') === '1');
  const [aadhaarFile, setAadhaarFile] = useState(null);
  const [aadhaarStatus, setAadhaarStatus] = useState({ loading: false, success: false, warnings: [], rawText: '', error: '' });
  const [showAiAccessModal, setShowAiAccessModal] = useState(false);
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    preferredLanguage: 'en',
    relationLabel: 'S/o',
    relationName: '',
    age: '',
    dateOfBirth: '',
    gender: 'Male',
    address: '',
    aadhaarName: '',
    aadhaarNumber: '',
  });
  const { summary: aiSummary } = useAiAccessSummary();
  const aadhaarInputRef = useRef(null);
  const aiLocked = aiSummary && !canUseAiNow(aiSummary);
  const advocateId = auth.currentUser?.uid || '';
  const clientsState = useFirestoreCollection({
    enabled: Boolean(advocateId),
    queryFactory: () => query(collection(db, 'clients'), where('advocate_id', '==', advocateId)),
    queryKey: [advocateId, 'client-details-client'],
  });
  const casesState = useFirestoreCollection({
    enabled: Boolean(advocateId),
    queryFactory: () => query(collection(db, 'cases'), where('advocate_id', '==', advocateId)),
    queryKey: [advocateId, 'client-details-cases'],
  });
  const paymentsState = useFirestoreCollection({
    enabled: Boolean(advocateId),
    queryFactory: () => query(collection(db, 'payments'), where('advocate_id', '==', advocateId)),
    queryKey: [advocateId, 'client-details-payments'],
  });
  const documentsState = useFirestoreCollection({
    enabled: Boolean(advocateId),
    queryFactory: () => query(collection(db, 'documents'), where('advocate_id', '==', advocateId)),
    queryKey: [advocateId, 'client-details-documents'],
  });

  const updateField = (key, value) => {
    setForm((current) => {
      const next = { ...current, [key]: value };
      if (key === 'dateOfBirth' && !current.age) {
        next.age = calculateAgeFromDateOfBirth(value);
      }
      return next;
    });
  };

  const requestAadhaarUpload = () => {
    if (aiLocked) {
      setShowAiAccessModal(true);
      return;
    }
    aadhaarInputRef.current?.click();
  };

  useEffect(() => {
    const nextClient = clientsState.data.find((clientRecord) => clientRecord.id === clientId) || null;
    const nextCases = (casesState.data || []).filter(
      (caseRecord) =>
        caseRecord.client_id === nextClient?.id ||
        caseRecord.client_name === nextClient?.name ||
        (nextClient?.email && caseRecord.client_email === nextClient.email) ||
        (nextClient?.phone && caseRecord.client_phone === nextClient.phone)
    );
    const caseNumbers = new Set(nextCases.map((caseRecord) => caseRecord.case_number));

    setClient(nextClient);
    setRelatedCases(nextCases);
    setRelatedPayments((paymentsState.data || []).filter((payment) => caseNumbers.has(payment.case_id)));
    setRelatedDocuments((documentsState.data || []).filter((documentRecord) => caseNumbers.has(documentRecord.case_id)));

    if (nextClient && !showEdit) {
      setForm({
        name: nextClient.name || '',
        phone: nextClient.phone || '',
        email: nextClient.email || '',
        preferredLanguage: nextClient.preferredLanguage || 'en',
        relationLabel: nextClient.relationLabel || 'S/o',
        relationName: nextClient.relationName || '',
        age: nextClient.age || '',
        dateOfBirth: nextClient.dateOfBirth || '',
        gender: nextClient.gender || 'Male',
        address: nextClient.address || '',
        aadhaarName: nextClient.aadhaarName || '',
        aadhaarNumber: nextClient.aadhaarNumber || '',
      });
    }
  }, [casesState.data, clientId, clientsState.data, documentsState.data, paymentsState.data, showEdit]);

  useEffect(() => {
    setShowEdit(searchParams.get('edit') === '1');
  }, [searchParams]);

  const handleSave = async (event) => {
    event.preventDefault();
    if (!client) return;
    setSaving(true);
    try {
      await updateClientProfile({
        clientId: client.id,
        advocateId: auth.currentUser?.uid,
        data: {
          ...form,
          draftReady: true,
        },
        aadhaarFile,
      });
      setAadhaarFile(null);
      setAadhaarStatus({ loading: false, success: false, warnings: [], rawText: '', error: '' });
      setShowEdit(false);
      setSearchParams((current) => {
        const next = new URLSearchParams(current);
        next.delete('edit');
        return next;
      });
    } finally {
      setSaving(false);
    }
  };

  const applyAadhaarFields = (extracted = {}) => {
    setForm((current) => ({
      ...current,
      name: current.name || extracted.name || extracted.aadhaarName || '',
      aadhaarName: extracted.aadhaarName || current.aadhaarName || '',
      aadhaarNumber: extracted.aadhaarNumber || current.aadhaarNumber || '',
      dateOfBirth: extracted.dateOfBirth || current.dateOfBirth || '',
      age: extracted.age || current.age || calculateAgeFromDateOfBirth(extracted.dateOfBirth || current.dateOfBirth || ''),
      gender: extracted.gender || current.gender || '',
      address: extracted.address || current.address || '',
    }));
  };

  const handleAadhaarUpload = async (file) => {
    setAadhaarFile(file || null);
    if (!file || !auth.currentUser?.uid) {
      setAadhaarStatus({ loading: false, success: false, warnings: [], rawText: '', error: '' });
      return;
    }

    setAadhaarStatus({ loading: true, success: false, warnings: [], rawText: '', error: '' });
    try {
      const result = await extractAadhaarDetails({ advocateId: auth.currentUser.uid, file });
      applyAadhaarFields(result.extracted);
      setAadhaarStatus({
        loading: false,
        success: result.success,
        warnings: result.warnings || [],
        rawText: result.extracted?.rawText || '',
        error: '',
      });
    } catch (error) {
      setAadhaarStatus({
        loading: false,
        success: false,
        warnings: [],
        rawText: '',
        error: error.message || t('aadhaarReadFailed'),
      });
    }
  };

  const loading =
    clientsState.loadingInitial ||
    casesState.loadingInitial ||
    paymentsState.loadingInitial ||
    documentsState.loadingInitial;
  const refreshing =
    clientsState.refreshing ||
    casesState.refreshing ||
    paymentsState.refreshing ||
    documentsState.refreshing;

  if (loading) {
    return (
      <PageShell title={t('clientDetails')} subtitle={t('loadingClientDetails')} showBack>
        <LoadingState label={t('loadingClientDetails')} />
      </PageShell>
    );
  }

  if (!client) {
    return (
      <PageShell title={t('clientDetails')} subtitle={t('clientsSubtitle')} showBack>
        <section className="panel">
          <p className="empty-state">{t('clientNotFound')}</p>
        </section>
      </PageShell>
    );
  }

  return (
    <PageShell title={client.name} subtitle={t('clientDetailsSubtitle')} showBack>
      {refreshing ? <p className="helper-text">{t('refreshingWorkspace', { defaultValue: 'Refreshing from your latest saved data...' })}</p> : null}
      <section className="hero-card case-hero">
        <div>
          <p className="eyebrow">{t('clientLabel')}</p>
          <h2>{client.name}</h2>
          {buildClientDraftingSummary(client).map((line) => <p key={`summary-${line}`}>{line}</p>)}
        </div>
        <div className="case-hero__meta">
          <span className="case-hero__progress">
            {isClientDraftReady(client) ? t('draftReady') : t('draftProfileIncomplete')}
          </span>
        </div>
      </section>

      <section className="panel">
        <div className="section-heading">
          <div>
            <p className="eyebrow">{t('clientDraftProfile')}</p>
            <h2>{t('legalIdentity')}</h2>
          </div>
          <button
            type="button"
            className="icon-button icon-button--accent"
            onClick={() => setSearchParams((current) => {
              const next = new URLSearchParams(current);
              if (showEdit) {
                next.delete('edit');
              } else {
                next.set('edit', '1');
              }
              return next;
            })}
            aria-label={showEdit ? t('closeEditClientProfile') : t('editClientProfile')}
            title={showEdit ? t('closeEditClientProfile') : t('editClientProfile')}
          >
            {showEdit ? <CloseIcon className="app-icon" /> : <LockIcon className="app-icon" />}
          </button>
        </div>

        {showEdit ? (
          <form onSubmit={handleSave}>
            <div className="form-grid">
              <div className="form-group full-span">
                <label>{t('aadhaarUploadPreferred')}:</label>
                <input
                  ref={aadhaarInputRef}
                  type="file"
                  accept="image/*,application/pdf"
                  className="visually-hidden"
                  onChange={(e) => handleAadhaarUpload(e.target.files?.[0] || null)}
                />
                <button type="button" className="button button--secondary" onClick={requestAadhaarUpload}>{t('startAadhaarOcr')}</button>
              </div>
              {aadhaarStatus.success ? <p className="inline-feedback full-span">{t('aadhaarReadSuccess')}</p> : null}
              {aadhaarStatus.error ? <p className="inline-feedback inline-feedback--error full-span">{aadhaarStatus.error}</p> : null}
              {aadhaarStatus.warnings.length ? (
                <div className="record-card full-span">
                  <strong>{t('aadhaarNeedsReview')}</strong>
                  {aadhaarStatus.warnings.map((warning) => <p key={warning} className="helper-text">{warning}</p>)}
                </div>
              ) : null}
              <div className="form-group"><label>{t('name')}:</label><input type="text" value={form.name} onChange={(e) => updateField('name', e.target.value)} required /></div>
              <div className="form-group"><label>{t('phone')}:</label><input type="text" value={form.phone} onChange={(e) => updateField('phone', e.target.value)} required /></div>
              <div className="form-group"><label>{t('email')}:</label><input type="email" value={form.email} onChange={(e) => updateField('email', e.target.value)} /></div>
              <div className="form-group"><label>{t('preferredLanguage')}:</label><select value={form.preferredLanguage} onChange={(e) => updateField('preferredLanguage', e.target.value)}><option value="en">{t('english')}</option><option value="hi">{t('hindi')}</option></select></div>
              <div className="form-group"><label>{t('relationLabel')}:</label><select value={form.relationLabel} onChange={(e) => updateField('relationLabel', e.target.value)}>{relationLabelOptions.map((option) => <option key={option} value={option}>{option}</option>)}</select></div>
              <div className="form-group"><label>{t('relationName')}:</label><input type="text" value={form.relationName} onChange={(e) => updateField('relationName', e.target.value)} required /></div>
              <div className="form-group"><label>{t('age')}:</label><input type="number" min="0" value={form.age} onChange={(e) => updateField('age', e.target.value)} required /></div>
              <div className="form-group"><label>{t('dateOfBirth')}:</label><input type="date" value={form.dateOfBirth} onChange={(e) => updateField('dateOfBirth', e.target.value)} required /></div>
              <div className="form-group"><label>{t('gender')}:</label><select value={form.gender} onChange={(e) => updateField('gender', e.target.value)}>{genderOptions.map((option) => <option key={option} value={option}>{t(option.toLowerCase())}</option>)}</select></div>
              <div className="form-group full-span"><label>{t('address')}:</label><textarea value={form.address} onChange={(e) => updateField('address', e.target.value)} required /></div>
              <div className="form-group"><label>{t('aadhaarName')}:</label><input type="text" value={form.aadhaarName} onChange={(e) => updateField('aadhaarName', e.target.value)} required /></div>
              <div className="form-group"><label>{t('aadhaarNumber')}:</label><input type="text" value={form.aadhaarNumber} onChange={(e) => updateField('aadhaarNumber', e.target.value)} required /></div>
              {aadhaarStatus.rawText ? <div className="form-group full-span"><label>{t('aadhaarOcrPreview')}:</label><textarea value={aadhaarStatus.rawText} readOnly rows="5" /></div> : null}
            </div>
            <button type="submit" className="button" disabled={saving}>{saving ? t('saving') : t('save')}</button>
          </form>
        ) : (
          <div className="record-list">
            <article className="record-item"><div><strong>{t('relationLabel')}</strong><p>{client.relationLabel} {client.relationName}</p></div></article>
            <article className="record-item"><div><strong>{t('age')} / {t('dateOfBirth')}</strong><p>{client.age} | {client.dateOfBirth}</p></div></article>
            <article className="record-item"><div><strong>{t('gender')}</strong><p>{client.gender}</p></div></article>
            <article className="record-item"><div><strong>{t('address')}</strong><p>{client.address}</p></div></article>
            <article className="record-item"><div><strong>{t('aadhaarName')}</strong><p>{client.aadhaarName}</p></div></article>
            <article className="record-item"><div><strong>{t('aadhaarNumber')}</strong><p>{client.aadhaarNumber}</p></div></article>
            {client.aadhaarReferenceUrl ? (
              <article className="record-item">
                <div>
                  <strong>{t('aadhaarReference')}</strong>
                  <p>{client.aadhaarReferenceName || t('documentDetails')}</p>
                </div>
                <a className="text-link" href={client.aadhaarReferenceUrl} target="_blank" rel="noopener noreferrer">{t('open')}</a>
              </article>
            ) : null}
          </div>
        )}
      </section>

      {aadhaarStatus.loading ? (
        <LoadingState overlay label={t('aadhaarProcessingTitle')}>
          <div className="loading-state__meta">
            <p>{t('aadhaarProcessingBody')}</p>
          </div>
        </LoadingState>
      ) : null}
      {showAiAccessModal ? (
        <div className="app-modal">
          <button type="button" className="app-modal__scrim" aria-label={t('closeNavigation')} onClick={() => setShowAiAccessModal(false)} />
          <div className="app-modal__surface">
            <p className="eyebrow">{t('aiAccessEyebrow')}</p>
            <h2>{t('aadhaarAiGateTitle')}</h2>
            <p>{t('aadhaarAiGateBody')}</p>
            <div className="workflow-defaults">
              <span>{aiSummary ? getAiCreditHeadline(aiSummary) : t('noAiCreditsLeft')}</span>
            </div>
            <div className="button-row top-space">
              <button type="button" className="button" onClick={() => navigate('/premium?feature=aadhaar_ocr')}>
                {t('manageAiAccess')}
              </button>
              <button type="button" className="button button--secondary" onClick={() => setShowAiAccessModal(false)}>
                {t('back')}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <section className="stats-grid">
        <article className="stat-card">
          <CasesIcon className="app-icon section-icon" />
          <strong>{relatedCases.length}</strong>
          <span>{t('cases')}</span>
        </article>
        <article className="stat-card">
          <PaymentsIcon className="app-icon section-icon" />
          <strong>{relatedPayments.length}</strong>
          <span>{t('payments')}</span>
        </article>
        <article className="stat-card">
          <DocumentsIcon className="app-icon section-icon" />
          <strong>{relatedDocuments.length}</strong>
          <span>{t('documents')}</span>
        </article>
      </section>

      <section className="panel">
        <div className="section-heading">
          <div>
            <p className="eyebrow">{t('currentMatters')}</p>
            <h2>{t('cases')}</h2>
          </div>
        </div>
        {relatedCases.length === 0 ? (
          <p className="empty-state">{t('clientCasesEmpty')}</p>
        ) : (
          <div className="record-list">
            {relatedCases.map((caseRecord) => (
              <article
                key={caseRecord.id}
                className="record-item record-item--interactive"
                onClick={() => navigate(`/cases/${caseRecord.id}`)}
              >
                <div>
                  <strong>{caseRecord.case_number}</strong>
                  <p>{caseRecord.summary || caseRecord.next_step || t('noSummaryAdded')}</p>
                </div>
                <ArrowRightIcon className="app-icon" />
              </article>
            ))}
          </div>
        )}
      </section>
    </PageShell>
  );
};

export default ClientDetails;
